/**
 * <RankingRow> — un élève dans le classement, en ligne d'une carte-liste (molecule).
 *
 * Son rang, son avatar, son nom affiché, son titre, son XP : ce que la ligne du classement montre
 * aujourd'hui. Comme `<TutorRow>`, elle ne dessine pas de carte : un classement est UNE
 * carte-liste. Elle ne presse pas, aujourd'hui non plus.
 *
 * `highlighted` est la ligne de l'élève lui-même : un fond et un liseré à la couleur de l'univers,
 * et son rang dit « ta place » à un lecteur d'écran.
 *
 * `<RankingGap>` est la coupure entre le bas de la liste et la ligne de l'élève, quand il est
 * classé plus loin que ce que la liste montre.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { formatAmount } from "../../utils/gameAmount"
import { CardListRow } from "../CardList"
import { type UIRankingEntry } from "../../../ports/primary/ui/types"

export type { UIRankingEntry }

export type RankingRowProps = {
    entry: UIRankingEntry
    /** La ligne de l'élève lui-même. Défaut `false`. */
    highlighted?: boolean
    testID?: string
}

/** L'avatar d'une ligne de classement : la taille de liste du livre. */
export const RANKING_AVATAR_SIZE = 44
/**
 * La colonne du rang, au moins : trois chiffres y tiennent, et les avatars s'alignent d'une ligne à
 * l'autre. Un rang plus long (au-delà de 999) élargit la sienne : un rang ne se coupe jamais.
 */
export const RANK_COLUMN_WIDTH = 28

export function RankingRow(props: RankingRowProps) {
    const { entry, highlighted = false, testID } = props
    const { rank, displayName, avatarUrl, title, xp } = entry

    const hasTitle = title != null && title !== ""
    const xpLabel = formatAmount(xp, "xp")

    return (
        <View
            testID={testID}
            accessible
            accessibilityLabel={[
                highlighted ? `Ta place : ${rank}` : `${rank}`,
                displayName,
                hasTitle ? title : null,
                xpLabel,
            ]
                .filter(Boolean)
                .join(", ")}
            style={highlighted ? styles.highlighted : undefined}
        >
            {highlighted && <View style={styles.rail} />}
            <CardListRow>
                <Row gap="3">
                    <Text
                        size={rank > 99 ? "text-xs" : "text-md"}
                        weight="extrabold"
                        color="primary"
                        center
                        style={styles.rank}
                    >
                        {rank}
                    </Text>
                    <Avatar uri={avatarUrl ?? undefined} name={displayName} size={RANKING_AVATAR_SIZE} />
                    <Stack gap="0" style={styles.body}>
                        <Text size="text-md" weight="bold" numberOfLines={1}>
                            {displayName}
                        </Text>
                        {hasTitle && (
                            <Text size="text-sm" color="text-muted" numberOfLines={1}>
                                {title}
                            </Text>
                        )}
                    </Stack>
                    <Text size="text-md" weight="bold" numberOfLines={1} style={styles.xp}>
                        {xpLabel}
                    </Text>
                </Row>
            </CardListRow>
        </View>
    )
}

/** Les rangs sautés entre le bas de la liste et la ligne de l'élève. */
export function RankingGap(props: { testID?: string }) {
    return (
        <CardListRow py="2" testID={props.testID}>
            <Row justify="center">
                <Icon name="ellipsis-h" size={20} color="text-muted" />
            </Row>
        </CardListRow>
    )
}

const styles = StyleSheet.create((theme) => ({
    highlighted: { backgroundColor: theme.colors["primary-soft"] },
    /** Le liseré de la ligne de l'élève, posé sur le bord sans décaler son contenu. */
    rail: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: 4,
        backgroundColor: theme.colors.role,
    },
    rank: { minWidth: RANK_COLUMN_WIDTH, flexShrink: 0 },
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    xp: { flexShrink: 0 },
}))
